import React, { useState, useEffect } from 'react';
import { format, isSameMonth, isToday } from 'date-fns';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  hourly_rate: number;
  email: string | null;
}

interface Shift {
  id: number;
  shift_date: string;
  am_emp_1: number | null;
  am_emp_2: number | null;
  pm_emp_1: number | null;
  pm_emp_2: number | null;
  notes: string | null;
  am_employee_1?: Employee | null;
  am_employee_2?: Employee | null;
  pm_employee_1?: Employee | null;
  pm_employee_2?: Employee | null;
  note_timestamp?: string;
  note_author?: string;
}

interface DayShifts {
  date: string;
  shift: Shift | null;
}

interface MonthShifts {
  month: number;
  year: number;
  days: DayShifts[];
}

interface EmployeeMonthlySummary {
  employee: Employee;
  total_hours: number;
  total_pay: number;
  shifts: Shift[];
}

const MyShifts: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const monthParam = searchParams.get('month');
  const yearParam = searchParams.get('year');
  
  // Simple function to get the current date in Central Time
  const getCurrentCentralDate = (): Date => {
    // This is a simplification - just subtract 6 hours from UTC for Central Time (ignoring DST)
    const now = new Date();
    // Central Time is UTC-6
    now.setHours(now.getHours() - 6);
    return now;
  };

  // Initialize with current central date or from URL params
  const initialDate = new Date();
  if (monthParam && yearParam) {
    initialDate.setMonth(parseInt(monthParam) - 1);
    initialDate.setFullYear(parseInt(yearParam));
  }

  // Initialize with current central date
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [shifts, setShifts] = useState<MonthShifts | null>(null);
  const [summary, setSummary] = useState<EmployeeMonthlySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'schedule' | 'notes' | 'pay'>('schedule');
  const [editingNote, setEditingNote] = useState<{ id: number; note: string; isEdit?: boolean } | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  
  // Add note form state - moved from renderNotesTab to fix hook rules
  const [showAddNoteForm, setShowAddNoteForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Add a state for cell click modal
  const [clickedDay, setClickedDay] = useState<{
    date: string;
    shift: Shift | null;
  } | null>(null);

  // Add a state for mobile viewport detection
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(false);
  
  // Add state for mobile note editing
  const [mobileNoteText, setMobileNoteText] = useState<string>("");
  
  // Add effect for handling window resize and mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobileViewport(window.innerWidth <= 580);
    };
    
    // Set initial value
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Add effect to initialize mobile note text when clicked day changes
  useEffect(() => {
    if (clickedDay?.shift && clickedDay.shift.notes !== undefined) {
      setMobileNoteText(clickedDay.shift.notes || "");
    }
  }, [clickedDay]);

  // First, fetch the employees for the dropdown
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        console.log('Fetching employees...');
        const response = await api.get('/employees');
        console.log('Employees fetched:', response.data);
        setEmployees(response.data);
      } catch (error) {
        console.error('Error fetching employees:', error);
        setErrorMessage('Failed to load employees. Please refresh the page.');
      }
    };
    fetchEmployees();
  }, []);

  // Create a consistent sort function to use throughout the component
  const sortDaysByDate = (days: DayShifts[]): DayShifts[] => {
    // Log dates before sorting for debugging
    console.log("Before sorting:", days.map(d => d.date));
    
    // First deduplicate days by date (keeping the first occurrence)
    const seenDates = new Set<string>();
    const uniqueDays = days.filter(day => {
      const dateKey = day.date.split('T')[0]; // Get just the date part
      if (seenDates.has(dateKey)) {
        console.log(`Removing duplicate date: ${dateKey}`);
        return false;
      }
      seenDates.add(dateKey);
      return true;
    });
    
    console.log(`After deduplication: ${uniqueDays.length} days (removed ${days.length - uniqueDays.length} duplicates)`);
    
    const sorted = [...uniqueDays].sort((a, b) => {
      // Extract just the date part for stable sorting
      const dateA = a.date.split('T')[0];
      const dateB = b.date.split('T')[0];
      
      // Simple string comparison for YYYY-MM-DD format
      return dateA.localeCompare(dateB);
    });
    
    // Log dates after sorting for debugging
    console.log("After sorting:", sorted.map(d => d.date));
    
    return sorted;
  };

  // Function to prepare employee display names with last initial if needed
  const getEmployeeDisplayName = (employee: Employee) => {
    if (!employee) return '';
    
    // Count how many employees have this first name
    const sameName = employees.filter(emp => 
      emp.first_name.toLowerCase() === employee.first_name.toLowerCase()
    ).length;
    
    // If there are multiple employees with the same first name, add last initial
    if (sameName > 1 && employee.last_name) {
      return `${employee.first_name} ${employee.last_name.charAt(0)}.`;
    }
    
    return employee.first_name;
  };
  
  // Sort employees by first name
  const sortedEmployees = [...employees].sort((a, b) => 
    a.first_name.localeCompare(b.first_name)
  );

  // Load data when component mounts or date changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setErrorMessage(null);
        
        // Get the month and year from current display date
        const month = currentDate.getMonth() + 1;
        const year = currentDate.getFullYear();

        console.log(`Fetching data for month: ${month}, year: ${year}`);
        
        // Check if shifts exist for this month, if not, create them
        try {
          const shiftsResponse = await api.get<MonthShifts>(`/shifts/employee-view?month=${month}&year=${year}`);
          if (shiftsResponse.data && (!shiftsResponse.data.days || shiftsResponse.data.days.length === 0)) {
            // No shifts exist, create them
            await api.post(`/shifts/shifts/create-month?month=${month}&year=${year}`);
            // Fetch again after creating
            const newShiftsResponse = await api.get<MonthShifts>(`/shifts/employee-view?month=${month}&year=${year}`);
            
            // Filter and sort immediately when setting shifts data
            const monthPrefix = `${year}-${month.toString().padStart(2, '0')}`;
            
            // First filter by month, then sort
            const filteredDays = newShiftsResponse.data.days.filter(day => day.date.startsWith(monthPrefix));
            const sortedDays = sortDaysByDate(filteredDays);
            
            setShifts({...newShiftsResponse.data, days: sortedDays});
          } else {
            // Filter and sort immediately when setting shifts data
            const monthPrefix = `${year}-${month.toString().padStart(2, '0')}`;
            
            // First filter by month, then sort
            const filteredDays = shiftsResponse.data.days.filter(day => day.date.startsWith(monthPrefix));
            const sortedDays = sortDaysByDate(filteredDays);
            
            setShifts({...shiftsResponse.data, days: sortedDays});
          }
        } catch (error) {
          console.error('Error checking shifts:', error);
          // Try to create shifts for this month
          await api.post(`/shifts/shifts/create-month?month=${month}&year=${year}`);
          // Fetch again after creating
          const newShiftsResponse = await api.get<MonthShifts>(`/shifts/employee-view?month=${month}&year=${year}`);
          
          // Filter and sort immediately when setting shifts data
          const monthPrefix = `${year}-${month.toString().padStart(2, '0')}`;
          
          // First filter by month, then sort
          const filteredDays = newShiftsResponse.data.days.filter(day => day.date.startsWith(monthPrefix));
          const sortedDays = sortDaysByDate(filteredDays);
          
          setShifts({...newShiftsResponse.data, days: sortedDays});
        }

        // Fetch current user's shifts and summary
        // Use selectedEmployeeId if admin is viewing as another employee
        let endpoint = `/shifts/my-shifts?month=${month}&year=${year}`;
        if (user?.is_admin && selectedEmployeeId) {
          endpoint += `&employee_id=${selectedEmployeeId}`;
        }
        
        const summaryResponse = await api.get<EmployeeMonthlySummary>(endpoint);
        setSummary(summaryResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setErrorMessage('Failed to load shift data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentDate, user, selectedEmployeeId]);

  // Simple helper to check if a date is today (in Central Time)
  const isCentralToday = (date: string): boolean => {
    const centralToday = getCurrentCentralDate();
    const shiftDate = new Date(date);
    
    return (
      shiftDate.getDate() === centralToday.getDate() &&
      shiftDate.getMonth() === centralToday.getMonth() &&
      shiftDate.getFullYear() === centralToday.getFullYear()
    );
  };
  
  // Simple helper to check if a date is in the current month based on string prefix
  const isInCurrentMonth = (date: string): boolean => {
    // Generate the month prefix we're looking for (e.g. "2025-03")
    const year = currentDate.getFullYear();
    const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
    const monthPrefix = `${year}-${month}`;
    
    // Check if the date string starts with our prefix
    return date.startsWith(monthPrefix);
  };

  // Helper to create calendar grid data
  const createCalendarGrid = () => {
    if (!shifts || !shifts.days || shifts.days.length === 0) return [];
    
    // Sort days by date
    const sortedDays = sortDaysByDate([...shifts.days]);
    
    // Get the month/year we're viewing
    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();
    
    // Format the first day of the month as string
    const firstOfMonthStr = `${year}-${month.toString().padStart(2, '0')}-01`;
    
    // Create a temporary date object just to get day of week (0-6, where 0 is Sunday)
    const firstDayOfWeek = new Date(firstOfMonthStr).getDay();
    
    // Get days in month (reliably without timezone issues)
    const daysInMonth = new Date(year, month, 0).getDate();
    
    // Calculate days from previous month to show
    const daysFromPrevMonth = firstDayOfWeek;
    
    // Calculate previous month and year
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    const daysInPrevMonth = new Date(prevYear, prevMonth, 0).getDate();
    
    // Calculate next month and year
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    
    // Create the calendar grid days array
    const calendarDays = [];
    
    // Add days from previous month (work with strings)
    for (let i = 0; i < daysFromPrevMonth; i++) {
      const prevMonthDay = daysInPrevMonth - daysFromPrevMonth + i + 1;
      const dateStr = `${prevYear}-${prevMonth.toString().padStart(2, '0')}-${prevMonthDay.toString().padStart(2, '0')}`;
      
      // Find the day in shifts data or create an empty one
      const day = shifts.days.find(d => d.date.startsWith(dateStr)) || { date: dateStr, shift: null };
      calendarDays.push({ ...day, currentMonth: false });
    }
    
    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = `${year}-${month.toString().padStart(2, '0')}-${i.toString().padStart(2, '0')}`;
      
      // Find the day in shifts data or create an empty one
      const day = shifts.days.find(d => d.date.startsWith(dateStr)) || { date: dateStr, shift: null };
      calendarDays.push({ ...day, currentMonth: true });
    }
    
    // Calculate how many days from next month to add to fill the grid
    // We want 6 rows of 7 days = 42 cells total
    const remainingCells = 42 - calendarDays.length;
    
    // Add days from next month
    for (let i = 1; i <= remainingCells; i++) {
      const dateStr = `${nextYear}-${nextMonth.toString().padStart(2, '0')}-${i.toString().padStart(2, '0')}`;
      
      // Find the day in shifts data or create an empty one  
      const day = shifts.days.find(d => d.date.startsWith(dateStr)) || { date: dateStr, shift: null };
      calendarDays.push({ ...day, currentMonth: false });
    }
    
    return calendarDays;
  };

  // Simplified month navigation
  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleAssignShift = async (shiftId: number, period: 'am' | 'pm', position: 1 | 2, employeeId: number) => {
    try {
      console.log('Assigning shift:', { shiftId, period, position, employeeId });
      // Don't set loading state - this causes the full page reload
      // setLoading(true);
      
      const response = await api.post(`/shifts/${shiftId}/assign`, {
        employee_id: employeeId,
        period: period,
        position: position
      });
      
      // Update the shifts data locally without reloading all data
      if (shifts) {
        const updatedDays = shifts.days.map(day => {
          if (day.shift && day.shift.id === shiftId) {
            return { ...day, shift: response.data };
          }
          return day;
        });
        
        // Sort days to ensure consistent order
        const sortedDays = sortDaysByDate(updatedDays);
        
        setShifts({ ...shifts, days: sortedDays });
        
        // Update summary in a targeted way if needed
        // We only need this if the current user's shifts changed
        const month = currentDate.getMonth() + 1;
        const year = currentDate.getFullYear();
        const summaryResponse = await api.get<EmployeeMonthlySummary>(`/shifts/my-shifts?month=${month}&year=${year}`);
        setSummary(summaryResponse.data);
        
        console.log('Employee assigned successfully');
      }
      // Don't set loading state to false either - we never set it to true
      // setLoading(false);
    } catch (error: any) {
      console.error('Error assigning shift:', error);
      
      // Handle conflict errors (409)
      if (error.response && error.response.status === 409) {
        // This was a conflict - another user edited the shift
        alert(error.response.data.detail);
        
        // Refresh only the specific shift data
        const shiftResponse = await api.get(`/shifts/${shiftId}`);
        
        // Update just this shift in state
        if (shifts) {
          const updatedDays = shifts.days.map(day => {
            if (day.shift && day.shift.id === shiftId) {
              return { ...day, shift: shiftResponse.data };
            }
            return day;
          });
          
          setShifts({ ...shifts, days: updatedDays });
        }
      } else {
        alert('Failed to assign employee. Please try again.');
      }
    }
  };

  const handleRemoveEmployee = async (shiftId: number, period: 'am' | 'pm', position: 1 | 2) => {
    try {
      console.log('Removing employee from shift:', { shiftId, period, position });
      // Don't set loading state - this causes the full page reload
      // setLoading(true);
      
      const response = await api.post(`/shifts/${shiftId}/assign`, {
        employee_id: null,
        period: period,
        position: position
      });
      
      // Update the shifts data locally without reloading all data
      if (shifts) {
        const updatedDays = shifts.days.map(day => {
          if (day.shift && day.shift.id === shiftId) {
            return { ...day, shift: response.data };
          }
          return day;
        });
        
        // Sort days to ensure consistent order
        const sortedDays = sortDaysByDate(updatedDays);
        
        setShifts({ ...shifts, days: sortedDays });
        
        // Update summary in a targeted way
        const month = currentDate.getMonth() + 1;
        const year = currentDate.getFullYear();
        const summaryResponse = await api.get<EmployeeMonthlySummary>(`/shifts/my-shifts?month=${month}&year=${year}`);
        setSummary(summaryResponse.data);
        
        console.log('Employee removed successfully');
      }
      // Don't set loading state to false either - we never set it to true
      // setLoading(false);
    } catch (error: any) {
      console.error('Error removing employee from shift:', error);
      
      // Handle conflict errors (409)
      if (error.response && error.response.status === 409) {
        // This was a conflict - another user edited the shift
        alert(error.response.data.detail);
        
        // Refresh only the specific shift data
        const shiftResponse = await api.get(`/shifts/${shiftId}`);
        
        // Update just this shift in state
        if (shifts) {
          const updatedDays = shifts.days.map(day => {
            if (day.shift && day.shift.id === shiftId) {
              return { ...day, shift: shiftResponse.data };
            }
            return day;
          });
          
          setShifts({ ...shifts, days: updatedDays });
        }
      } else {
        alert('Failed to remove employee. Please try again.');
      }
    }
  };

  // Add the fetchCurrentMonthData function - remove any unneeded calls to this function
  const fetchCurrentMonthData = async () => {
    try {
      setLoading(true);
      // Get the month and year from current display date
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      
      // Fetch updated shifts data
      const shiftsResponse = await api.get<MonthShifts>(`/shifts/employee-view?month=${month}&year=${year}`);
      
      // Sort days consistently
      const sortedDays = sortDaysByDate(shiftsResponse.data.days);
      
      setShifts({...shiftsResponse.data, days: sortedDays});
      
      // Also refresh the summary
      const summaryResponse = await api.get<EmployeeMonthlySummary>(`/shifts/my-shifts?month=${month}&year=${year}`);
      setSummary(summaryResponse.data);
      setLoading(false);
    } catch (error) {
      console.error('Error refreshing data:', error);
      setLoading(false);
    }
  };

  // Create a specialized function for updating notes without reloading
  const handleLocalUpdateNotes = async (shiftId: number, notes: string) => {
    try {
      // Don't set global loading state
      
      // Prepare data with just the notes field
      const noteData = { 
        notes: notes.trim()
      };
      
      console.log(`Locally updating notes for shift ${shiftId}:`, noteData);
      
      // Call API to update the notes
      const response = await api.patch(`/shifts/${shiftId}/notes`, noteData);
      console.log('Note update response:', response.data);
      
      // Update shifts data with new notes and metadata
      if (shifts) {
        const updatedDays = shifts.days.map(day => {
          if (day.shift && day.shift.id === shiftId) {
            return { 
              ...day, 
              shift: { 
                ...day.shift,
                notes: response.data.notes,
                note_timestamp: response.data.note_timestamp,
                note_author: response.data.note_author
              } 
            };
          }
          return day;
        });
        
        // Update state without re-sorting which can cause flickering
        setShifts({ ...shifts, days: updatedDays });
      }
      
      // Update clickedDay state if this is the current shift in the modal
      if (clickedDay && clickedDay.shift && clickedDay.shift.id === shiftId) {
        setClickedDay({
          ...clickedDay,
          shift: {
            ...clickedDay.shift,
            notes: response.data.notes,
            note_timestamp: response.data.note_timestamp,
            note_author: response.data.note_author
          }
        });
      }
      
      // Clear editing state
      setEditingNote(null);
      
      return true;
    } catch (error) {
      console.error('Error updating notes locally:', error);
      alert('Failed to update notes. Please try again.');
      return false;
    }
  };

  // Add a mobile-specific employee assignment handler to avoid reloads
  const handleMobileAssignShift = async (shiftId: number, period: 'am' | 'pm', position: 1 | 2, employeeId: number) => {
    try {
      console.log('Mobile: Assigning shift:', { shiftId, period, position, employeeId });
      // Don't set full loading state to prevent screen flicker
      
      const response = await api.post(`/shifts/${shiftId}/assign`, {
        employee_id: employeeId,
        period: period,
        position: position
      });
      
      // Only update the specific shift in the modal
      if (clickedDay && clickedDay.shift && clickedDay.shift.id === shiftId) {
        // Update the clickedDay with the updated shift data
        setClickedDay({
          ...clickedDay,
          shift: response.data
        });
      }
      
      // Also update in the shifts data locally
      if (shifts) {
        const updatedDays = shifts.days.map(day => {
          if (day.shift && day.shift.id === shiftId) {
            return { ...day, shift: response.data };
          }
          return day;
        });
        
        // Keep the modal open - don't force a state change by re-sorting
        setShifts({ ...shifts, days: updatedDays });
      }
      
    } catch (error: any) {
      console.error('Error assigning shift in mobile view:', error);
      
      // Handle conflict errors (409)
      if (error.response && error.response.status === 409) {
        alert(error.response.data.detail);
        
        // Refresh only this specific shift
        try {
          const shiftResponse = await api.get(`/shifts/${shiftId}`);
          
          // Update the clicked day
          if (clickedDay && clickedDay.shift && clickedDay.shift.id === shiftId) {
            setClickedDay({
              ...clickedDay,
              shift: shiftResponse.data
            });
          }
          
          // Update in the shifts data
          if (shifts) {
            const updatedDays = shifts.days.map(day => {
              if (day.shift && day.shift.id === shiftId) {
                return { ...day, shift: shiftResponse.data };
              }
              return day;
            });
            
            setShifts({ ...shifts, days: updatedDays });
          }
        } catch (refreshError) {
          console.error('Error refreshing shift data:', refreshError);
        }
      } else {
        alert('Failed to assign employee. Please try again.');
      }
    }
  };

  // Add a desktop-specific employee assignment handler to avoid reloads (similar to mobile version)
  const handleLocalAssignShift = async (shiftId: number, period: 'am' | 'pm', position: 1 | 2, employeeId: number) => {
    try {
      console.log('Desktop: Locally assigning shift:', { shiftId, period, position, employeeId });
      // Don't set global loading state to prevent page reload
      
      const response = await api.post(`/shifts/${shiftId}/assign`, {
        employee_id: employeeId,
        period: period,
        position: position
      });
      
      // Update in the shifts data locally without triggering a full reload
      if (shifts) {
        const updatedDays = shifts.days.map(day => {
          if (day.shift && day.shift.id === shiftId) {
            return { ...day, shift: response.data };
          }
          return day;
        });
        
        // Update state without re-sorting which can cause flickering
        setShifts({ ...shifts, days: updatedDays });
      }
      
    } catch (error: any) {
      console.error('Error assigning shift in desktop view:', error);
      
      // Handle conflict errors (409)
      if (error.response && error.response.status === 409) {
        alert(error.response.data.detail);
        
        // Refresh only this specific shift
        try {
          const shiftResponse = await api.get(`/shifts/${shiftId}`);
          
          // Update in the shifts data
          if (shifts) {
            const updatedDays = shifts.days.map(day => {
              if (day.shift && day.shift.id === shiftId) {
                return { ...day, shift: shiftResponse.data };
              }
              return day;
            });
            
            setShifts({ ...shifts, days: updatedDays });
          }
        } catch (refreshError) {
          console.error('Error refreshing shift data:', refreshError);
        }
      } else {
        alert('Failed to assign employee. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-red-500">{errorMessage}</div>
      </div>
    );
  }
  
  // Get the current user info from the summary
  const currentUser = summary?.employee || null;
  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">User data not available</div>
      </div>
    );
  }

  // Render the calendar view with distinct desktop and mobile displays
  const renderCalendarView = () => {
    if (!shifts || !shifts.days || shifts.days.length === 0) {
      return <div className="text-center p-4">No schedule data available</div>;
    }
    
    const calendarDays = createCalendarGrid();
    const currentUser = summary?.employee;
    
    if (!currentUser) {
      return <div className="text-center p-4">User data not available</div>;
    }
    
    return (
      <div className={`mt-1 ${isMobileViewport ? 'mobile-view' : 'desktop-view'}`}>
        <div className="grid grid-cols-7 gap-0.5">
          {/* Weekday headers */}
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
            <div key={day} className="text-center font-semibold py-0.5 bg-gray-100 text-xs">
              {day}
            </div>
          ))}
          
          {/* Calendar days */}
          {calendarDays.map((day, index) => {
            // Get date parts from the string date (YYYY-MM-DD)
            const dateStr = day.date.split('T')[0];
            const [yearStr, monthStr, dayStr] = dateStr.split('-');
            const dayOfMonth = parseInt(dayStr, 10);
            
            const isToday = isCentralToday(day.date);
            
            // Check if current user is assigned to this day
            const isUserAssignedAM = day.shift && (
              day.shift.am_emp_1 === currentUser.id || 
              day.shift.am_emp_2 === currentUser.id
            );
            
            const isUserAssignedPM = day.shift && (
              day.shift.pm_emp_1 === currentUser.id || 
              day.shift.pm_emp_2 === currentUser.id
            );
            
            const isUserAssigned = isUserAssignedAM || isUserAssignedPM;
            
            // Get background color based on shift assignment
            let bgColorClass = !day.currentMonth ? 'bg-gray-50 text-gray-400' : '';
            if (isUserAssignedAM && isUserAssignedPM) {
              bgColorClass = 'bg-gradient-to-br from-purple-50 to-blue-50 border-purple-300';
            } else if (isUserAssignedAM) {
              bgColorClass = 'bg-green-50 border-green-300';
            } else if (isUserAssignedPM) {
              bgColorClass = 'bg-blue-50 border-blue-300';
            }
            
            // Handle clicks on date cell for mobile and desktop
            const handleDateCellClick = () => {
              // No need to recalculate mobile view here
              // const mobileCheck = typeof window !== 'undefined' && window.innerWidth <= 580;
              // console.log(`Cell Clicked! isMobileView: ${mobileCheck}, viewMode: ${viewMode}, Day Date:`, day.date);
              
              // Simply log with the shared state
              console.log(`Cell Clicked! isMobileView: ${isMobileViewport}, viewMode: ${viewMode}, Day Date:`, day.date);
              
              // Always set clickedDay when this cell is clicked, regardless of viewport size
              setClickedDay(day);
            };
            
            // Build desktop employee display with AM/PM labels and dropdowns
            const renderDesktopEmployees = () => {
              if (!day.shift) return null;
              
              const shift = day.shift;
              
              return (
                <div>
                  {/* AM Section */}
                  <div className="mt-1">
                    <div className="text-xs text-gray-600 font-medium">AM</div>
                    <div className="space-y-0.5">
                      <select
                        value={shift.am_emp_1 || ''}
                        onChange={(e) => handleAssignShift(shift.id, 'am', 1, parseInt(e.target.value) || 0)}
                        className="w-full border-0 bg-green-50 text-xs p-0.5 rounded"
                      >
                        <option value="">-</option>
                        {sortedEmployees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {getEmployeeDisplayName(emp)}
                          </option>
                        ))}
                      </select>
                      
                      <select
                        value={shift.am_emp_2 || ''}
                        onChange={(e) => handleAssignShift(shift.id, 'am', 2, parseInt(e.target.value) || 0)}
                        className="w-full border-0 bg-green-50 text-xs p-0.5 rounded"
                      >
                        <option value="">-</option>
                        {sortedEmployees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {getEmployeeDisplayName(emp)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* PM Section */}
                  <div className="mt-1">
                    <div className="text-xs text-gray-600 font-medium">PM</div>
                    <div className="space-y-0.5">
                      <select
                        value={shift.pm_emp_1 || ''}
                        onChange={(e) => handleAssignShift(shift.id, 'pm', 1, parseInt(e.target.value) || 0)}
                        className="w-full border-0 bg-blue-50 text-xs p-0.5 rounded"
                      >
                        <option value="">-</option>
                        {sortedEmployees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {getEmployeeDisplayName(emp)}
                          </option>
                        ))}
                      </select>
                      
                      <select
                        value={shift.pm_emp_2 || ''}
                        onChange={(e) => handleAssignShift(shift.id, 'pm', 2, parseInt(e.target.value) || 0)}
                        className="w-full border-0 bg-blue-50 text-xs p-0.5 rounded"
                      >
                        <option value="">-</option>
                        {sortedEmployees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {getEmployeeDisplayName(emp)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Notes section for desktop view */}
                  {(shift.notes || user?.is_admin) && (
                    <div className="mt-1">
                      <div 
                        className={`text-xs p-1 rounded ${shift.notes ? 'bg-yellow-50' : 'bg-gray-50'} cursor-pointer`}
                        onClick={() => setEditingNote({
                          id: shift.id,
                          note: shift.notes || ''
                        })}
                      >
                        {shift.notes ? (
                          <div>
                            <div className="line-clamp-2 text-[9px]">{shift.notes}</div>
                            {shift.note_timestamp && (
                              <div className="text-[8px] text-gray-500 italic">
                                {format(new Date(shift.note_timestamp), 'HH:mm')} 
                                {shift.note_author && ` by ${shift.note_author}`}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-gray-400 text-[9px]">+ Add note</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            };
            
            // Build mobile employee display with color coded blocks
            const renderMobileEmployees = () => {
              if (!day.shift) return null;
              
              const shift = day.shift;
              const hasAnyEmployee = shift.am_employee_1 || shift.am_employee_2 || shift.pm_employee_1 || shift.pm_employee_2;
              
              if (!hasAnyEmployee) return null;
              
              return (
                <div className="mobile-employee-display">
                  {/* AM Section */}
                  {(shift.am_employee_1 || shift.am_employee_2) && (
                    <div className="mobile-shift-section am-section">
                      {shift.am_employee_1 && (
                        <div className="am-shift mobile-employee">
                          {getEmployeeDisplayName(shift.am_employee_1)}
                        </div>
                      )}
                      {shift.am_employee_2 && (
                        <div className="am-shift mobile-employee">
                          {getEmployeeDisplayName(shift.am_employee_2)}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* PM Section */}
                  {(shift.pm_employee_1 || shift.pm_employee_2) && (
                    <div className="mobile-shift-section pm-section">
                      {shift.pm_employee_1 && (
                        <div className="pm-shift mobile-employee">
                          {getEmployeeDisplayName(shift.pm_employee_1)}
                        </div>
                      )}
                      {shift.pm_employee_2 && (
                        <div className="pm-shift mobile-employee">
                          {getEmployeeDisplayName(shift.pm_employee_2)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            };
            
            // Helper to get just first initial and last initial
            const getEmployeeShortName = (employee: Employee) => {
              const firstInitial = employee.first_name ? employee.first_name.charAt(0) : '';
              const lastInitial = employee.last_name ? employee.last_name.charAt(0) : '';
              return `${firstInitial}${lastInitial}`;
            };
            
            return (
              <div 
                key={day.date} 
                className={`p-0.5 border border-gray-200 overflow-hidden transition-colors ${isMobileViewport ? 'mobile-clickable' : ''} 
                  ${bgColorClass}
                  ${isToday ? 'border-blue-500 border-2' : ''}
                `}
                // Only make dates clickable in mobile view
                onClick={isMobileViewport ? handleDateCellClick : undefined}
              >
                <div className="flex justify-between items-center">
                  <div className={`text-xs font-medium ${isToday ? 'text-blue-600' : ''}`}>{dayOfMonth}</div>
                  {isUserAssigned && (
                    <div className={`text-xs font-medium px-0.5 text-center rounded-sm
                      ${isUserAssignedAM && isUserAssignedPM ? 'bg-purple-200 text-purple-900' : 
                        isUserAssignedAM ? 'bg-green-200 text-green-900' :
                        'bg-blue-200 text-blue-900'}`}
                    >
                      {isUserAssignedAM && isUserAssignedPM ? 'A+P' : isUserAssignedAM ? 'AM' : 'PM'}
                    </div>
                  )}
                </div>
                
                {day.currentMonth && day.shift && (
                  isMobileViewport ? renderMobileEmployees() : renderDesktopEmployees()
                )}
                
                {/* Notes indicator for mobile ONLY - don't show in desktop view */}
                {isMobileViewport && day.shift && day.shift.notes && (
                  <div className="mt-0.5 h-1.5 bg-yellow-400 rounded-sm" title={day.shift.notes}></div>
                )}
              </div>
            );
          })}
        </div>

        {/* Modal for mobile employee assignment - ONLY in mobile view */}
        {clickedDay && isMobileViewport && renderMobileAssignModal()}
        
        {/* Modal for note editing in desktop view */}
        {editingNote && !isMobileViewport && viewMode === 'calendar' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-4 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Edit Note</h3>
                <button
                  onClick={() => setEditingNote(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              
              <textarea
                value={editingNote.note}
                onChange={(e) => setEditingNote({ ...editingNote, note: e.target.value })}
                className="w-full border border-gray-300 rounded p-2 h-40 mb-4"
                placeholder="Enter note here..."
              />
              
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setEditingNote(null)}
                  className="px-3 py-1 bg-gray-200 text-gray-700 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleLocalUpdateNotes(editingNote.id, editingNote.note);
                    setEditingNote(null);
                  }}
                  className="px-3 py-1 bg-blue-600 text-white rounded"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Restore the renderShiftCell function for dropdown
  const renderShiftCell = (shift: Shift, period: 'am' | 'pm', position: 1 | 2) => {
    const fieldMap = {
      'am-1': { field: 'am_emp_1', employee: shift.am_employee_1 },
      'am-2': { field: 'am_emp_2', employee: shift.am_employee_2 },
      'pm-1': { field: 'pm_emp_1', employee: shift.pm_employee_1 },
      'pm-2': { field: 'pm_emp_2', employee: shift.pm_employee_2 }
    };
    
    const key = `${period}-${position}` as keyof typeof fieldMap;
    const { employee } = fieldMap[key];
    
    // Use the shared state instead of recalculating
    // const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
    // const isMobileView = viewportWidth <= 580;

    // Use the current user ID from the summary to highlight cells correctly
    const cellClasses = `p-1 border text-center text-xs ${
      employee?.id === currentUser.id ? 'bg-green-100' : ''
    } hover:bg-gray-100 mobile-employee-cell`;

    // For mobile devices OR when in mobile viewport size, show clickable cell instead of dropdown
    if (isMobileViewport) {
      // For mobile view, just return the content, not a nested td
      return (
        <td 
          className={cellClasses}
          onClick={() => {
            console.log("Mobile shift cell clicked!");
            setClickedDay({ date: shift.shift_date, shift: shift });
          }}
        >
          {employee ? getEmployeeDisplayName(employee) : '-'}
        </td>
      );
    }

    // For desktop, keep the dropdown
    return (
      <td className={cellClasses}>
        <select
          value={employee?.id || ''}
          onChange={(e) => handleAssignShift(shift.id, period, position, parseInt(e.target.value))}
          className="w-full border-0 bg-transparent text-xs p-0"
          style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          <option value="">-</option>
          {sortedEmployees.map((emp) => (
            <option key={emp.id} value={emp.id}>
              {getEmployeeDisplayName(emp)}
            </option>
          ))}
        </select>
      </td>
    );
  };

  // Update the note cell rendering
  const renderNotesCell = (shift: Shift | null) => {
    if (!shift) {
      return <td className="p-1 border"></td>;
    }

    if (isMobileViewport) {
      // On mobile, we don't want a notes column at all
      // This was removed as intended by the user
      return null;
    }

    if (editingNote && editingNote.id === shift.id) {
      return (
        <td className="p-1 border">
          <textarea
            value={editingNote.note}
            onChange={(e) => setEditingNote({ ...editingNote, note: e.target.value })}
            className="w-full h-10 p-1 border rounded text-xs"
            placeholder="Enter note here..."
          />
          <div className="flex justify-end gap-1 mt-1">
            <button
              onClick={() => handleLocalUpdateNotes(shift.id, editingNote.note)}
              className="px-1 py-0.5 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
              disabled={loading}
            >
              Save
            </button>
            <button
              onClick={() => setEditingNote(null)}
              className="px-1 py-0.5 bg-gray-200 rounded text-xs hover:bg-gray-300"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </td>
      );
    }

    return (
      <td 
        onClick={() => setEditingNote({
          id: shift.id,
          note: shift.notes || ''
        })}
        className={`p-1 border cursor-pointer text-xs ${
          shift.notes 
            ? 'bg-yellow-50 hover:bg-yellow-100' 
            : 'text-gray-500 hover:bg-gray-50'
        }`}
      >
        <div className="text-xs line-clamp-1">{shift.notes || '+ Add note'}</div>
        {shift.notes && shift.note_timestamp && shift.note_author && (
          <div className="text-[9px] text-gray-500 italic mt-0.5 hidden sm:block">
            {format(new Date(shift.note_timestamp), 'HH:mm')} by {shift.note_author}
          </div>
        )}
      </td>
    );
  };

  // Update the handleDateClick function to ensure it works properly
  const handleDateClick = (day: DayShifts) => {
    if (day.shift) {
      setClickedDay(day);
    } else {
      // If there's no shift yet but we have a date, we should still open the modal
      setClickedDay({
        date: day.date,
        shift: null
      });
    }
  };

  // New component for mobile employee assignment
  const renderMobileAssignModal = () => {
    console.log("renderMobileAssignModal called with clickedDay:", clickedDay, "isMobileViewport:", isMobileViewport);
    
    if (!clickedDay) {
      console.log("renderMobileAssignModal: No clickedDay, returning null");
      return null;
    }

    const shift = clickedDay.shift;
    const dateObj = new Date(clickedDay.date);
    const formattedDate = format(dateObj, 'EEE, MMM d, yyyy');

    if (!shift) {
      console.log("renderMobileAssignModal: No shift, returning empty shift modal");
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-4 w-full max-w-sm">
            <h3 className="text-lg font-medium mb-2">{formattedDate}</h3>
            <p className="mb-4 text-gray-600">No shift data available for this date.</p>
            <div className="flex justify-end">
              <button
                onClick={() => setClickedDay(null)}
                className="px-3 py-1 bg-blue-600 text-white rounded text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg p-4 w-full max-w-sm">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-medium">{formattedDate}</h3>
            <button
              onClick={() => setClickedDay(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          
          <div className="mb-4">
            {/* Legend for color coding */}
            <div className="mobile-grid-legend mb-2">
              <div className="am-legend">AM Shift (4 hours) - Green</div>
              <div className="pm-legend">PM Shift (2 hours) - Blue</div>
            </div>
          
            {/* AM Shift Section with green background */}
            <div className="bg-green-50 p-3 rounded-lg mb-3 border border-green-200">
              <div className="font-medium text-sm mb-2 flex items-center text-green-800">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                AM Shift (4 hours)
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-700 block mb-1">Employee 1</label>
                  <select
                    value={shift.am_emp_1 || ""}
                    onChange={(e) => handleMobileAssignShift(shift.id, 'am', 1, parseInt(e.target.value) || 0)}
                    className="w-full border border-green-200 rounded text-sm p-1 bg-white"
                  >
                    <option value="">- None -</option>
                    {sortedEmployees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {getEmployeeDisplayName(emp)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-700 block mb-1">Employee 2</label>
                  <select
                    value={shift.am_emp_2 || ""}
                    onChange={(e) => handleMobileAssignShift(shift.id, 'am', 2, parseInt(e.target.value) || 0)}
                    className="w-full border border-green-200 rounded text-sm p-1 bg-white"
                  >
                    <option value="">- None -</option>
                    {sortedEmployees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {getEmployeeDisplayName(emp)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            {/* PM Shift Section with blue background */}
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <div className="font-medium text-sm mb-2 flex items-center text-blue-800">
                <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
                PM Shift (2 hours)
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-700 block mb-1">Employee 1</label>
                  <select
                    value={shift.pm_emp_1 || ""}
                    onChange={(e) => handleMobileAssignShift(shift.id, 'pm', 1, parseInt(e.target.value) || 0)}
                    className="w-full border border-blue-200 rounded text-sm p-1 bg-white"
                  >
                    <option value="">- None -</option>
                    {sortedEmployees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {getEmployeeDisplayName(emp)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-700 block mb-1">Employee 2</label>
                  <select
                    value={shift.pm_emp_2 || ""}
                    onChange={(e) => handleMobileAssignShift(shift.id, 'pm', 2, parseInt(e.target.value) || 0)}
                    className="w-full border border-blue-200 rounded text-sm p-1 bg-white"
                  >
                    <option value="">- None -</option>
                    {sortedEmployees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {getEmployeeDisplayName(emp)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="text-xs text-gray-700 flex items-center mb-1">
              <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
              Notes
            </label>
            <textarea
              value={mobileNoteText}
              onChange={(e) => setMobileNoteText(e.target.value)}
              className="w-full border border-gray-300 rounded text-sm p-2 h-20"
              placeholder="Add notes here..."
            />
          </div>
          
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setClickedDay(null)}
              className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm"
            >
              Close
            </button>
            <button
              onClick={() => {
                handleLocalUpdateNotes(shift.id, mobileNoteText).then(success => {
                  if (success) setClickedDay(null);
                });
              }}
              className="px-3 py-1 bg-blue-600 text-white rounded text-sm"
            >
              Save & Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // New function to render the Pay tab
  const renderPayTab = () => {
    if (!summary) {
      return <div>No payment data available</div>;
    }
    
    // Convert hourly rate to number to ensure toFixed works
    const hourlyRate = Number(summary.employee.hourly_rate);
    
    return (
      <div className="mt-4">
        <h2 className="text-lg font-semibold mb-4">My Payment Summary</h2>
        
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 bg-blue-50">
            <h3 className="text-lg font-medium text-blue-800">
              {format(currentDate, 'MMMM yyyy')}
            </h3>
      </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-sm text-gray-500">Total Hours</div>
                <div className="text-3xl font-bold text-gray-800">{summary.total_hours}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-sm text-gray-500">Hourly Rate</div>
                <div className="text-3xl font-bold text-gray-800">${hourlyRate.toFixed(2)}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-sm text-gray-500">Monthly Earnings</div>
                <div className="text-3xl font-bold text-blue-700">${Number(summary.total_pay).toFixed(2)}</div>
            </div>
            </div>
            
            <h3 className="text-md font-semibold mb-3 text-gray-700">Payment Breakdown</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Shift</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Pay</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {summary.shifts.map(shift => {
                    // Calculate hours and pay for this specific shift
                    let hours = 0;
                    const isAM = shift.am_emp_1 === currentUser.id || shift.am_emp_2 === currentUser.id;
                    const isPM = shift.pm_emp_1 === currentUser.id || shift.pm_emp_2 === currentUser.id;
                    
                    // AM shift logic - if both slots filled, split the hours
                    if (isAM) {
                      if (shift.am_emp_1 && shift.am_emp_2) {
                        hours += 2; // 4 hours split between 2 people
                      } else {
                        hours += 4; // Solo AM shift
                      }
                    }
                    
                    // PM shift logic
                    if (isPM) {
                      if (shift.pm_emp_1 && shift.pm_emp_2) {
                        hours += 1; // 2 hours split between 2 people
                      } else {
                        hours += 2; // Solo PM shift
                      }
                    }
                    
                    const pay = hours * hourlyRate;
                    
                    // Get the date part only
                    const dateStr = shift.shift_date.split('T')[0];
                    const [year, month, dayOfMonth] = dateStr.split('-').map(Number);
                    
                    return (
                      <tr key={shift.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-base text-gray-900">
                          {`${month}/${dayOfMonth}/${year}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-base text-gray-900">
                          {isAM && isPM ? 'AM + PM' : isAM ? 'AM' : 'PM'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-base text-gray-900">
                          {hours}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-base font-medium text-gray-900">
                          ${pay.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-base font-medium text-gray-900" colSpan={2}>Total</td>
                    <td className="px-6 py-4 whitespace-nowrap text-base font-medium text-gray-900">{summary.total_hours}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-base font-medium text-blue-700">${Number(summary.total_pay).toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Update the Notes tab render function to show notes in a simple list format
  const renderNotesTab = () => {
    // Filter to only days that have shifts with notes and sort by date
    const daysWithNotes = shifts?.days
      .filter(day => day.shift && day.shift.notes && day.shift.notes.trim() !== '')
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()) || [];
    
    return (
      <div className="mt-4">
        <h3 className="text-lg font-semibold mb-4">Notes for {format(currentDate, 'MMMM yyyy')}</h3>
        {daysWithNotes.length === 0 ? (
          <p className="text-gray-500">No notes found for this month.</p>
        ) : (
          <div className="space-y-1">
            {daysWithNotes.map((day) => {
              const date = new Date(day.date);
              return (
                <div key={day.date} className="text-sm">
                  {format(date, 'MMM d')} - {day.shift?.notes}
                  {day.shift?.note_timestamp && day.shift?.note_author && (
                    <span className="text-xs text-gray-500 italic ml-1">
                      ({format(new Date(day.shift.note_timestamp), 'HH:mm')} by {day.shift.note_author})
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="bg-white rounded-lg shadow p-4 md:p-6">
        {/* Section title and tabs */}
        <div className="flex flex-col md:flex-row md:items-center md:space-x-4 mb-4 border-b pb-4">
          <h1 className="text-xl font-bold mb-2 md:mb-0">Barn Management</h1>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveTab('schedule')}
              className={`px-3 py-1 rounded-lg text-sm md:text-base font-medium ${
                activeTab === 'schedule' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              My Shifts
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`px-3 py-1 rounded-lg text-sm md:text-base font-medium ${
                activeTab === 'notes' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Notes
            </button>
            <button
              onClick={() => setActiveTab('pay')}
              className={`px-3 py-1 rounded-lg text-sm md:text-base font-medium ${
                activeTab === 'pay' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              My Pay
            </button>
          </div>
        </div>

        {/* Month navigation - mobile-friendly */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
          <div className="flex items-center mb-2 md:mb-0">
            {activeTab === 'schedule' && <h2 className="text-base md:text-lg font-semibold">Monthly Schedule</h2>}
            {activeTab === 'notes' && <h2 className="text-base md:text-lg font-semibold">Shift Notes</h2>}
            {activeTab === 'pay' && <h2 className="text-base md:text-lg font-semibold">Payment Reports</h2>}
          </div>
          
          <div className="flex flex-wrap items-center gap-2 md:gap-3 w-full md:w-auto">
            {/* Employee Selector (admins only) */}
            {user?.is_admin && (
              <div className="flex items-center mb-2 md:mb-0 w-full md:w-auto md:mr-3">
                <span className="mr-2 text-xs md:text-sm">View as:</span>
                <select
                  value={selectedEmployeeId || ''}
                  onChange={(e) => setSelectedEmployeeId(e.target.value ? Number(e.target.value) : null)}
                  className="p-1 border rounded text-xs md:text-sm flex-grow md:flex-grow-0"
                >
                  <option value="">Current user</option>
                  {sortedEmployees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {getEmployeeDisplayName(emp)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="flex items-center justify-between w-full md:w-auto">
              <button
                onClick={handlePreviousMonth}
                className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 text-xs md:text-sm"
              >
                &lt; Prev
              </button>
              <div className="font-semibold text-xs md:text-sm mx-2">
                {format(currentDate, 'MMM yyyy')}
              </div>
              <button
                onClick={handleNextMonth}
                className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 text-xs md:text-sm"
              >
                Next &gt;
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center p-4">Loading...</div>
        ) : (
          <>
            {activeTab === 'schedule' && (
              <>
                <div className="flex justify-end mb-4">
                  <div className="inline-flex rounded-md shadow-sm" role="group">
                    <button
                      type="button"
                      onClick={() => setViewMode('calendar')}
                      className={`px-3 py-1 text-xs md:text-sm font-medium rounded-l-lg ${
                        viewMode === 'calendar'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      } border border-gray-200`}
                    >
                      Calendar
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode('list')}
                      className={`px-3 py-1 text-xs md:text-sm font-medium rounded-r-lg ${
                        viewMode === 'list'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      } border-t border-r border-b border-gray-200`}
                    >
                      List
                    </button>
                  </div>
                </div>
                
                {viewMode === 'calendar' ? (
                  renderCalendarView()
                ) : (
                  <div className="overflow-x-auto w-full">
                    <table className="min-w-full border border-gray-300">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="p-1 border text-xs" style={{ width: '40px' }}>DATE</th>
                          <th className="p-1 border text-xs" style={{ width: '60px' }}>AM 1</th>
                          <th className="p-1 border text-xs" style={{ width: '60px' }}>AM 2</th>
                          <th className="p-1 border text-xs" style={{ width: '60px' }}>PM 1</th>
                          <th className="p-1 border text-xs" style={{ width: '60px' }}>PM 2</th>
                          {!isMobileViewport && <th className="p-1 border text-xs" style={{ width: '80px' }}>Notes</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {shifts?.days.map((day) => {
                          // Just get the date part (YYYY-MM-DD) from the shift_date string
                          const dateStr = day.date.split('T')[0];
                          const [year, month, dayOfMonth] = dateStr.split('-').map(Number);
                          
                          // Handle clicks on date cell for mobile and desktop
                          const handleDateCellClick = () => {
                            console.log(`Cell Clicked in LIST VIEW! isMobileViewport: ${isMobileViewport}, viewMode: ${viewMode}, Day Date:`, day.date);
                            
                            // Always set clickedDay when this cell is clicked, regardless of viewport size
                            setClickedDay(day);
                          };
                          
                          return (
                            <tr
                              key={dateStr}
                              className={`${isCentralToday(day.date) ? 'bg-blue-50' : ''}`}
                            >
                              <td 
                                className="p-1 border text-center text-xs mobile-clickable"
                                onClick={isMobileViewport ? handleDateCellClick : undefined}
                              >
                                <div>{`${month}/${dayOfMonth}`}</div>
                                {/* Add yellow indicator for notes in mobile view */}
                                {isMobileViewport && day.shift?.notes && (
                                  <div className="mt-0.5 h-1.5 bg-yellow-400 rounded-sm" title={day.shift.notes}></div>
                                )}
                              </td>
                              {day.shift ? (
                                <>
                                  {renderShiftCell(day.shift, 'am', 1)}
                                  {renderShiftCell(day.shift, 'am', 2)}
                                  {renderShiftCell(day.shift, 'pm', 1)}
                                  {renderShiftCell(day.shift, 'pm', 2)}
                                  {!isMobileViewport && renderNotesCell(day.shift)}
                                </>
                              ) : (
                                <>
                                  <td className="p-1 border text-center text-xs">-</td>
                                  <td className="p-1 border text-center text-xs">-</td>
                                  <td className="p-1 border text-center text-xs">-</td>
                                  <td className="p-1 border text-center text-xs">-</td>
                                  {!isMobileViewport && <td className="p-1 border text-center text-xs">-</td>}
                                </>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    
                    {/* Modal for mobile employee assignment - ONLY in mobile view */}
                    {clickedDay && isMobileViewport && renderMobileAssignModal()}
                  </div>
                )}
              </>
            )}
            {activeTab === 'notes' && renderNotesTab()}
            {activeTab === 'pay' && renderPayTab()}
          </>
        )}
      </div>
    </div>
  );
};

export default MyShifts;